/**
 * Group Recruiting Meeting — top-level page.
 *
 * Class component (per the project pattern; some MobX-driven page mounts
 * misbehave as functional components in this codebase). Uses an autorun to
 * bridge MobX observables to React re-renders via forceUpdate().
 *
 * On mount: probe /leader-session for an authed leader. If 200, jump
 * straight to the meeting view (no identity verification). Otherwise, show
 * the IdentityVerification screen.
 */

import React from 'react'
import { autorun } from 'mobx'
import UserProfileService from './../../shared/services/UserProfile.service'

import IdentityVerification from './components/IdentityVerification/IdentityVerification.component'
import MeetingRoom from './components/MeetingRoom/MeetingRoom.component'
import EnrollmentSplit from './components/EnrollmentSplit/EnrollmentSplit.component'

import Store from './RecruitingMeetingPage.store'
import formatMeetingStartLocal from './formatStartTime'

import './RecruitingMeetingPage.scss'

class RecruitingMeetingPage extends React.Component {
  _disposeAutorun = null

  componentDidMount() {
    // Bridge MobX → React re-renders.
    let isFirstRun = true
    this._disposeAutorun = autorun(() => {
      void Store.viewState
      void Store.verifying
      void Store.identifierInput
      void Store.errorMessage
      void Store.isLeaderProbe
      void Store.leaderEmptyReason
      void Store.zoomLive
      void Store.zoomEnded
      void (Store.meetingContext && Store.meetingContext.prospectFirstName)

      if (isFirstRun) {
        isFirstRun = false
        return
      }
      this.forceUpdate()
    })

    // If the URL carries ?force_prospect=1, skip the leader-session probe so
    // an authenticated leader can manually exercise the prospect flow. This
    // is for testing and for the rare case where a leader booked a slot as
    // a prospect (e.g., bringing a friend).
    const forceProspect = /(\?|&)force_prospect=1\b/.test(window.location.search)

    // If the visitor is already authenticated, probe /leader-session.
    // Otherwise show identity verification immediately.
    const userId = UserProfileService.getUserId && UserProfileService.getUserId()
    if (userId && !forceProspect) {
      Store.tryLeaderSession()
    } else {
      Store.skipLeaderProbe()
    }
  }

  componentWillUnmount() {
    if (this._disposeAutorun) {
      this._disposeAutorun()
      this._disposeAutorun = null
    }
  }

  renderProbing() {
    return (
      <div className='rm-page__probe'>
        <div className='rm-page__probe-spinner' aria-hidden='true' />
        <p>Loading…</p>
      </div>
    )
  }

  renderLeaderIdle() {
    let msg
    if (Store.leaderEmptyReason === 'meeting_completed') {
      msg = "Today's Meeting has Completed"
    } else if (Store.leaderEmptyReason === 'meeting_upcoming') {
      const when = formatMeetingStartLocal(Store.leaderUpcomingStartsAt)
      msg = when ? `The meeting will start at ${when}` : "Today's Meeting is Coming Up"
    } else {
      msg = 'No Meeting Today'
    }
    return (
      <div className='rm-page__notice'>
        <div className='rm-page__notice-card'>
          <h2>{msg}</h2>
        </div>
      </div>
    )
  }

  renderBody() {
    const { viewState, meetingContext } = Store

    if (Store.isLeaderProbe) return this.renderProbing()

    if (viewState === 'leader_idle') return this.renderLeaderIdle()

    if (viewState === 'verifying') {
      return <IdentityVerification />
    }

    // 'waiting' | 'live' | 'enrolling' | 'ended' — all show the meeting view,
    // with EnrollmentSplit overlaying the right pane when 'enrolling'.
    const showSplit = viewState === 'enrolling' && meetingContext && !meetingContext.isLeader
    return (
      <div className={`rm-page__meeting ${showSplit ? 'rm-page__meeting--split' : ''}`}>
        <MeetingRoom />
        {showSplit && <EnrollmentSplit />}
      </div>
    )
  }

  render() {
    // Only apply the meeting-styled <main> wrapper for actual meeting
    // states. The leader-idle / verifying / probing screens use the default
    // centered card layout instead.
    const inMeetingFlow = !Store.isLeaderProbe && ['waiting', 'live', 'enrolling', 'ended'].includes(Store.viewState)
    // Auto-collapse the header once the SDK has actually connected — there's
    // no point keeping the brand band in front of an active meeting.
    const headerCollapsed = inMeetingFlow && Store.zoomLive
    const pageClasses = ['rm-page', headerCollapsed ? 'rm-page--header-collapsed' : ''].filter(Boolean).join(' ')
    return (
      <div className={pageClasses}>
        <header className='rm-page__header'>
          <img
            className='rm-page__logo'
            src='https://firebasestorage.googleapis.com/v0/b/totemic-bonus-239316.appspot.com/o/static%2Flogos%2Flogo.usabg-website.jpg?alt=media'
            alt='USABG'
          />
          <span className='rm-page__title'>Career Opportunity</span>
        </header>
        <main className={`rm-page__main ${inMeetingFlow ? 'rm-page__main--meeting' : ''}`}>{this.renderBody()}</main>
      </div>
    )
  }
}

export default RecruitingMeetingPage
